import { Box, Container, Slider, Switch, Typography } from "@mui/material";
import { ValueAndDateTime } from "../../dashboard/models/dados-indicadores.model";
import { RopChart } from "../../../main/components/RopChart";
import { useEffect, useRef, useState } from "react";
import { getHourAndMinutes } from "../../../main/themes/defaultLayout";
import { EChartsOption } from "echarts";
import {
  alignSeriesValuesByDateTime,
  buildPotenciaChartMinuteTimeline,
  formatIrradianceTooltipValue,
  getChartHourLabel,
  getInitialSliderRange,
  mapSeriesValues,
  mapSeriesXLabels,
  toEchartsLineData,
} from "../../dashboard/helpers/chart-time-alignment";

export type PortfolioEvolutionChartProps = {
  energiaGeradaAtDayByHour: ValueAndDateTime[]
  irradianciaMediaByHour: ValueAndDateTime[]
  irradianciaMediaAtMinutes: ValueAndDateTime[]
  potenciaAtivaAtMinutes: ValueAndDateTime[]
};

export default function PortfolioEvolutionChart({
  energiaGeradaAtDayByHour,
  irradianciaMediaByHour,
  irradianciaMediaAtMinutes,
  potenciaAtivaAtMinutes,
}: PortfolioEvolutionChartProps) {

  const elementRef = useRef(null);

  const [showPotenciaAtivaInCharts, setShowPotenciaAtivaInCharts] =
    useState(false);

  const legendData = !showPotenciaAtivaInCharts
    ? ["Energia Gerada (MWh)", "Irradiância (W/m2)"]
    : ["Potência Ativa (MW)", "Irradiância Média (MW/m2)"];


  const potenciaTimeline = buildPotenciaChartMinuteTimeline(
    potenciaAtivaAtMinutes,
    irradianciaMediaAtMinutes
  );

  const potenciaAtivaSeriesKey =
    potenciaTimeline.map((item) => item.dateTime).join("|");

  const [potenciaAtivaXLabels, setPotenciaAtivaXLabels] = useState(() =>
    mapSeriesXLabels(potenciaTimeline)
  );

  const xLabels = !showPotenciaAtivaInCharts
    ? energiaGeradaAtDayByHour?.map((item: ValueAndDateTime) =>
      getHourAndMinutes(item.dateTime ?? "")
    )
    : potenciaAtivaXLabels;

  const evolutionValues = mapSeriesValues(energiaGeradaAtDayByHour);
  const filterIrradiance = irradianciaMediaByHour?.filter((item) =>
    energiaGeradaAtDayByHour?.some((e) => e.dateTime === item.dateTime)
  );
  const irradianceValues = mapSeriesValues(filterIrradiance);

  const [potenciaAtivaAtMinutesValues, setPotenciaAtivaAtMinutesValues] =
    useState<(number | null)[]>(() =>
      alignSeriesValuesByDateTime(potenciaTimeline, potenciaAtivaAtMinutes)
    );

  const [irradianceAtMinutes, setIrradianceAtMinutes] = useState<(number | null)[]>(
    () =>
      alignSeriesValuesByDateTime(
        potenciaTimeline,
        irradianciaMediaAtMinutes
      )
  );

  const option: EChartsOption = {
    title: {
      text: "Evolução da Geração",
      textStyle: {
        fontSize: 12,
        fontWeight: "bold",
      },
    },
    backgroundColor: "transparent",
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "cross",
        crossStyle: {
          color: "#999",
        },
      },
      confine: true,
    },
    grid: {
      left: '3%',
      right: '3%',
      bottom: '3%',
      top: '30%',
      containLabel: true
    },
    legend: {
      data: legendData,
      left: "right",
    },
    xAxis: [
      {
        type: "category",
        data: xLabels,
        axisPointer: {
          type: "shadow",
        },
      },
    ],
    yAxis: !showPotenciaAtivaInCharts
      ? [
        {
          type: "value",
          name: "MWh",
        },
        {
          type: "value",
          name: "(W/m2)",
        },
      ]
      : [
        {
          type: "value",
          name: "(MW)",
        },
        {
          type: "value",
          name: "(MW/m2)",
        },
      ],
    series: !showPotenciaAtivaInCharts
      ? [
        {
          name: "Energia Gerada (MWh)",
          type: "bar",
          itemStyle: {
            color: "#EE7D00",
          },
          tooltip: {
            valueFormatter: function (value) {
              let vPtBr: string;
              if (typeof value !== "number") {
                vPtBr = `${value}`;
              } else {
                vPtBr = value.toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
              }
              return `${vPtBr} MWh`;
            },
          },
          data: evolutionValues,
        },
        {
          name: "Irradiância (W/m2)",
          yAxisIndex: 1,
          itemStyle: {
            color: "#FAB900",
          },
          type: "line",
          connectNulls: false,
          areaStyle: {
            opacity: 0.3
          },
          tooltip: {
            valueFormatter: (value) => formatIrradianceTooltipValue(value, "W/m2"),
          },
          data: irradianceValues,
        },
      ]
      : [
        {
          name: "Potência Ativa (MW)",
          type: "line",
          yAxisIndex: 0,
          itemStyle: {
            color: "#EE7D00",
          },
          tooltip: {
            valueFormatter: function (value) {
              if (value == null || typeof value !== "number") {
                return "sem dado";
              }
              const vPtBr = value.toLocaleString("pt-BR", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
              return `${vPtBr} MW`;
            },
          },
          data: toEchartsLineData(potenciaAtivaAtMinutesValues),
        },
        {
          name: "Irradiância Média (MW/m2)",
          yAxisIndex: 1,
          itemStyle: {
            color: "#FAB900",
          },
          type: "line",
          connectNulls: false,
          tooltip: {
            valueFormatter: (value) => formatIrradianceTooltipValue(value, "MW/m2"),
          },
          data: toEchartsLineData(irradianceAtMinutes),
        },
      ],
  };

  const minDistance = 10;

  const maxSliderValue = potenciaTimeline.length;

  const [sliderValue, setSliderValue] = useState<number[]>(() =>
    getInitialSliderRange(maxSliderValue)
  );

  useEffect(() => {
    setSliderValue(getInitialSliderRange(maxSliderValue));
  }, [maxSliderValue, potenciaAtivaSeriesKey]);

  const handleChangeSlider = (
    _event: Event,
    newValue: number | number[],
    activeThumb: number,
  ) => {
    if (!Array.isArray(newValue)) {
      return;
    }

    if (activeThumb === 0) {
      setSliderValue([Math.min(newValue[0], sliderValue[1] - minDistance), sliderValue[1]]);
    } else {
      setSliderValue([sliderValue[0], Math.max(newValue[1], sliderValue[0] + minDistance)]);
    }
  };

  function valuetext(index: number) {
    const current = potenciaTimeline[index - 1];
    if (current)
      return getChartHourLabel(current.dateTime);
    return `${index}`
  }

  useEffect(() => {
    if (!potenciaTimeline.length) {
      setIrradianceAtMinutes([]);
      setPotenciaAtivaAtMinutesValues([]);
      setPotenciaAtivaXLabels([]);
      return;
    }

    const minIndex = sliderValue[0] - 1;
    const maxIndex = sliderValue[1] - 1;

    const alignedPower = alignSeriesValuesByDateTime(
      potenciaTimeline,
      potenciaAtivaAtMinutes
    );
    const alignedIrradiance = alignSeriesValuesByDateTime(
      potenciaTimeline,
      irradianciaMediaAtMinutes
    );
    const filteredPower = alignedPower.filter(
      (_item, i) => i >= minIndex && i <= maxIndex
    );
    const filteredIrradiance = alignedIrradiance.filter(
      (_item, i) => i >= minIndex && i <= maxIndex
    );
    const xLabelsAll = mapSeriesXLabels(potenciaTimeline);
    const filteredXLabels = xLabelsAll.filter(
      (_item, i) => i >= minIndex && i <= maxIndex
    );

    setPotenciaAtivaAtMinutesValues(filteredPower);
    setIrradianceAtMinutes(filteredIrradiance);
    setPotenciaAtivaXLabels(filteredXLabels);
  }, [sliderValue, potenciaAtivaSeriesKey, potenciaAtivaAtMinutes, irradianciaMediaAtMinutes]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
        marginBottom={1}
      >
        <Typography sx={{ marginRight: "4px", fontSize: "12px" }}>
          Energia Gerada
        </Typography>
        <Switch
          size="small"
          checked={showPotenciaAtivaInCharts}
          onChange={() => {
            setShowPotenciaAtivaInCharts(!showPotenciaAtivaInCharts)
          }}
          inputProps={{ "aria-label": "controlled" }}
        />
        <Typography sx={{ marginLeft: "4px", fontSize: "12px" }}>
          Potência Ativa
        </Typography>
      </Box>
      <Container
        ref={elementRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          height: showPotenciaAtivaInCharts ? "255px" : "90%",
          width: "100%",
        }}
      >
        <RopChart option={option} />
      </Container>
      {showPotenciaAtivaInCharts && maxSliderValue > 0 &&
        <Box>
          <Slider
            sx={() => ({
              width: "180px",
              height: 15,
              '& .MuiSlider-thumb': {
                width: 6,
                height: 25,
                borderRadius: 2
              },
              '& .MuiSlider-rail': {
                opacity: 0.28,
              }
            })}
            min={1}
            max={maxSliderValue}
            valueLabelFormat={valuetext}
            value={sliderValue}
            onChange={handleChangeSlider}
            valueLabelDisplay="auto"
            disableSwap
          /></Box>}
    </>
  )
}